import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  Bars3Icon,
  BookmarkSquareIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Card, Badge } from '@/components/common';
import { cn } from '@/utils';
import {
  MICROSITE_DEVICES,
  type MicrositeDevice,
  type MicrositeSection,
  type SectionTypeDef,
} from '@/services/microsite';
import { SchemaField } from './SectionFields';
import { IconBtn } from './SectionFields';
import { isShownOn, visibilitySummary } from './sectionUtils';

const DEVICE_ICONS: Record<MicrositeDevice, React.ElementType> = {
  desktop: ComputerDesktopIcon,
  tablet: DeviceTabletIcon,
  mobile: DevicePhoneMobileIcon,
};

const ALL_DEVICES = MICROSITE_DEVICES.map((d) => d.value);

export interface SectionCardProps {
  def: SectionTypeDef | undefined;
  section: MicrositeSection;
  index: number;
  total: number;
  editable: boolean;
  onChange: (next: MicrositeSection) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onToggleHidden: () => void;
  onToggleDevice: (device: MicrositeDevice) => void;
  onSaveAsTemplate: () => void;
  onRemove: () => void;
}

/**
 * One section in the builder: a drag handle, the visibility controls, and the
 * schema-driven config form.
 *
 * Drag-and-drop is the fast path, but the up/down buttons stay. `useSortable`
 * does give the handle keyboard support, and both are wired here — losing the
 * buttons would mean losing the only control that is obvious without first
 * discovering that the handle is focusable, which is an accessibility
 * regression dressed up as a cleanup.
 */
export function SectionCard(props: SectionCardProps) {
  const { section, editable } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    disabled: !editable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Lift the dragged card above its neighbours without reordering the DOM.
    zIndex: isDragging ? 30 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <SectionCardBody
        {...props}
        isDragging={isDragging}
        dragHandleProps={editable ? { ...attributes, ...listeners } : {}}
      />
    </div>
  );
}

function SectionCardBody({
  def,
  section,
  index,
  total,
  editable,
  onChange,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onToggleHidden,
  onToggleDevice,
  onSaveAsTemplate,
  onRemove,
  isDragging,
  dragHandleProps,
}: SectionCardProps & {
  isDragging: boolean;
  dragHandleProps: Record<string, unknown>;
}) {
  const setField = (field: string, value: unknown) => {
    const config = { ...section.config };
    if (value === undefined || value === '') {
      delete config[field];
    } else {
      config[field] = value;
    }
    onChange({ ...section, config });
  };

  const hidden = section.hidden === true;

  return (
    <Card
      className={cn(
        'overflow-hidden p-0 transition-shadow',
        isDragging && 'shadow-xl ring-2 ring-blue-400',
        hidden && 'opacity-70',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="flex min-w-0 items-center gap-2">
          {editable && (
            <button
              type="button"
              {...dragHandleProps}
              title="Drag to reorder (or focus and use the arrow keys)"
              aria-label={`Reorder ${def?.label ?? section.type}`}
              className="cursor-grab rounded-md p-1.5 text-gray-400 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 active:cursor-grabbing dark:hover:bg-gray-700"
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
          )}
          <span className="font-mono text-xs text-gray-400">{index + 1}</span>
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {def?.label ?? section.type}
          </span>
          {hidden && (
            <Badge variant="default" size="sm">
              Hidden
            </Badge>
          )}
          {!def && (
            <Badge variant="warning" size="sm">
              Unknown type: {section.type}
            </Badge>
          )}
        </div>

        {editable && (
          <div className="flex items-center gap-1">
            {/* Per-breakpoint visibility. Enforced server-side: an off device
                never receives the markup, so this really does save the bytes. */}
            <div
              className="mr-1 flex items-center gap-0.5 rounded-md border border-gray-200 px-0.5 dark:border-gray-600"
              role="group"
              aria-label="Show this section on"
            >
              {MICROSITE_DEVICES.map(({ value, label }) => {
                const Icon = DEVICE_ICONS[value];
                const on = isShownOn(section, value);
                return (
                  <button
                    key={value}
                    type="button"
                    role="switch"
                    aria-checked={on}
                    title={`${on ? 'Shown' : 'Hidden'} on ${label.toLowerCase()} — click to ${on ? 'hide' : 'show'}`}
                    aria-label={`${label}: ${on ? 'shown' : 'hidden'}`}
                    onClick={() => onToggleDevice(value)}
                    className={cn(
                      'rounded p-1 transition-colors',
                      on
                        ? 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                        : 'text-gray-300 line-through hover:bg-gray-200 dark:text-gray-600 dark:hover:bg-gray-700',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>

            <IconBtn
              onClick={onToggleHidden}
              title={hidden ? 'Show this section' : 'Hide without deleting'}
              active={hidden}
            >
              {hidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </IconBtn>
            <IconBtn onClick={onDuplicate} title="Duplicate this section">
              <DocumentDuplicateIcon className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={onSaveAsTemplate} title="Save as a reusable template">
              <BookmarkSquareIcon className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={onMoveUp} disabled={index === 0} title="Move up">
              <ArrowUpIcon className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={onMoveDown} disabled={index === total - 1} title="Move down">
              <ArrowDownIcon className="h-4 w-4" />
            </IconBtn>
            <IconBtn onClick={onRemove} title="Remove section" danger>
              <TrashIcon className="h-4 w-4" />
            </IconBtn>
          </div>
        )}
      </div>

      <p className="border-b border-gray-100 bg-gray-50/60 px-3 py-1 text-xs text-gray-400 dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-500">
        {visibilitySummary(section, ALL_DEVICES)}
      </p>

      <div className="space-y-4 p-4">
        {def ? (
          Object.entries(def.schema).map(([field, token]) => (
            <SchemaField
              key={field}
              sectionType={section.type}
              sectionId={section.id}
              field={field}
              token={token}
              value={section.config[field]}
              editable={editable}
              onChange={(v) => setField(field, v)}
            />
          ))
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This section type is not in the current registry, so its fields cannot be edited here.
          </p>
        )}
      </div>
    </Card>
  );
}
